import React from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterAltOff as FilterAltOffIcon,
} from '@mui/icons-material';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { SEARCE_COLORS } from '../../theme';
import { TABLE_STYLES } from '../../constants/tableStyles';
import type { BillingColumnPresetId } from '../../utils/billingTableConfig';
import type { BillingQuickFilter } from '../../utils/billingTableFilters';
import { BillingColumnVisibilityMenu } from './BillingColumnVisibilityMenu';
import TableCsvExportButton from '../table/TableCsvExportButton';

const QUICK_FILTER_OPTIONS: { id: BillingQuickFilter; label: string }[] = [
  { id: 'flagged', label: 'Flagged' },
  { id: 'over_budget', label: 'Over budget' },
  { id: 'missing_currency', label: 'Missing currency' },
];

export interface SandboxBillingToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  quickFilters: Set<BillingQuickFilter>;
  onQuickFilterToggle: (filter: BillingQuickFilter) => void;
  showClearFilters: boolean;
  onClearAllFilters: () => void;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onPresetApply: (preset: BillingColumnPresetId) => void;
  onExport: () => void;
  exportDisabled: boolean;
  totalCount: number;
  filteredCount: number;
  visibleColumnCount: number;
}

export function SandboxBillingToolbar({
  search,
  onSearchChange,
  quickFilters,
  onQuickFilterToggle,
  showClearFilters,
  onClearAllFilters,
  columnVisibility,
  onColumnVisibilityChange,
  onPresetApply,
  onExport,
  exportDisabled,
  totalCount,
  filteredCount,
  visibleColumnCount,
}: SandboxBillingToolbarProps): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2, flexShrink: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          placeholder="Search accounts…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: '1 1 220px', maxWidth: 360 }}
        />
        {QUICK_FILTER_OPTIONS.map((opt) => (
          <Chip
            key={opt.id}
            label={opt.label}
            size="small"
            clickable
            color={quickFilters.has(opt.id) ? 'primary' : 'default'}
            variant={quickFilters.has(opt.id) ? 'filled' : 'outlined'}
            onClick={() => onQuickFilterToggle(opt.id)}
          />
        ))}
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          ...TABLE_STYLES.tableToolbarTop,
          pb: 2,
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 1.25,
          borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
          backgroundColor: SEARCE_COLORS.white,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', minWidth: 0 }}>
          <Chip
            size="small"
            label={`Showing ${filteredCount} of ${totalCount}`}
            variant="outlined"
            sx={{ borderColor: alpha(SEARCE_COLORS.primary, 0.3) }}
          />
          <Chip size="small" label={`${visibleColumnCount} columns`} variant="outlined" />
          <TableCsvExportButton onClick={onExport} disabled={exportDisabled} />
          {showClearFilters && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOffIcon />}
              onClick={onClearAllFilters}
              sx={{
                color: SEARCE_COLORS.primary,
                borderColor: SEARCE_COLORS.primary,
                flexShrink: 0,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                  borderColor: SEARCE_COLORS.primary,
                },
              }}
            >
              Clear filters
            </Button>
          )}
        </Box>
        <BillingColumnVisibilityMenu
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
          onPresetApply={onPresetApply}
        />
      </Box>
    </Box>
  );
}
